import { useForm } from "react-hook-form";
import { Navigate } from "react-router-dom";

import { T, useT } from "@repo/transifex";
import { Button, Field, LayoutError, LayoutLogo } from "~/components";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useLogin } from "~/hooks/queries/auth";
import { useDeviceData } from "~/providers/app";
import { useAuth } from "~/providers/store/auth";
import { isApiError } from "@repo/system";
import { getFirebaseAuthErrorMessage } from "~/lib/firebase/auth-errors";
import { getSellRouteForTill } from "~/utils/tills/is-unattended-till";
import { ScreenLoader } from "~/components/screen/screen-loader";
import {
  ScreenError,
  ScreenErrorText,
  ScreenErrorTitle
} from "~/components/screen/screen-error";
import { DisplayErrorMessage } from "~/components/display-error-message";

type LoginInputs = {
  email: string;
  password: string;
};

export const LoginPage = () => {
  const t = useT();

  const { deviceId, tillId: deviceTillId } = useDeviceData();
  const { mutate: login, status, error, reset } = useLogin();
  const { status: authStatus, tillId } = useAuth();
  const {
    data: till,
    status: tillStatus,
    error: tillError,
    refetch: refetchTill
  } = useCurrentTill();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginInputs>();

  // if the user is already authenticated wait to get the till data and don't redirect anywhere
  if (authStatus !== "none" && tillId && tillStatus === "pending") {
    return (
      <ScreenLoader>
        <ScreenLoader.Title>
          <T _str="Loading till" />
        </ScreenLoader.Title>

        <ScreenLoader.Text>
          <T _str="Please wait while we load your till" />
        </ScreenLoader.Text>
      </ScreenLoader>
    );
  }

  if (tillStatus === "error") {
    return (
      <ScreenError onClose={() => void refetchTill()}>
        <ScreenErrorTitle>
          <T _str="Error loading till" />
        </ScreenErrorTitle>

        <ScreenErrorText>
          <DisplayErrorMessage error={tillError} />
        </ScreenErrorText>
      </ScreenError>
    );
  }

  // For unattended status, redirect to the appropriate sell page or profiles if till is not found
  if (authStatus === "unattended") {
    return (
      <Navigate replace to={till ? getSellRouteForTill(till) : "/profiles"} />
    );
  }

  if (authStatus !== "none") {
    return <Navigate replace to="/home" />;
  }

  let errorMessage;
  if (status === "error") {
    const firebaseMessage = getFirebaseAuthErrorMessage(error, t);
    if (firebaseMessage) {
      errorMessage = firebaseMessage;
    } else if (isApiError(error)) {
      errorMessage = error.body.message;
    } else {
      errorMessage = t("Something went wrong");
    }
  } else {
    errorMessage = undefined;
  }

  if (status === "error" && isApiError(error) && error.status !== 401) {
    return (
      <LayoutError error={errorMessage}>
        <Button
          onClick={() => {
            reset();
          }}
        >
          <T _str="Go back" />
        </Button>
      </LayoutError>
    );
  }

  return (
    <LayoutLogo>
      <div className="flex w-full flex-col items-center justify-center">
        <h1 className="text-3xl">
          <T _str="Welcome back!" />
        </h1>

        <p className="text-text-secondary">
          <T _str="Log in to your account" />
        </p>

        <form
          autoComplete="on"
          className="my-8 w-full max-w-xs"
          onSubmit={handleSubmit(({ email, password }) => {
            if (!deviceId) {
              throw new Error("Device ID is missing");
            }

            login({
              email,
              password,
              deviceId,
              tillId: deviceTillId ?? undefined
            });
          })}
        >
          <Field
            error={errors.email?.message ?? errorMessage}
            label={t("Email")}
            placeholder={t("Email...")}
            {...register("email", { required: t("Email is required") })}
          />

          <Field
            error={errors.password?.message ?? errorMessage}
            label={t("Password")}
            placeholder={t("Password...")}
            type="password"
            {...register("password", { required: t("Password is required") })}
          />

          <Button
            className="mt-8 w-full"
            loading={status === "pending"}
            type="submit"
          >
            <T _str="Login" />
          </Button>
        </form>
      </div>
    </LayoutLogo>
  );
};
